import React from 'react';
import { osc, wobble, drawOn, stagger, loop, clamp, lerp, easeOut } from '../../core/AnimationUtils';

/*
 * Math concept-acting SCENES (Anivaze v2) — the concept happens right in front
 * of the viewer like a cartoon, not a diagram. Kindergarten-friendly: bright,
 * rounded, characters that react, minimal text. Pure function of `t`.
 *
 * FractionsScene  — a pizza gets sliced, pieces lift to show what a fraction IS.
 * CountingScene   — apples drop into a basket one by one while the count climbs.
 */

/* ━━━ Fractions: a pizza is sliced, pieces lift to show 1/2 → 1/4 → 3/4 ━━━━━ */
export interface FractionsSceneProps {
  opacity?: number;
  accentColor?: string;
  /** Short friendly caption. */
  label?: string;
  /** Seconds since the scene started. */
  t?: number;
}

export const FractionsScene: React.FC<FractionsSceneProps> = ({
  opacity = 1,
  accentColor = '#f97316',
  t = 0,
  label = 'A fraction is a piece of the whole!',
}) => {
  /*
   * Full loop: ~8 s divided into 3 phases
   *   0–2.5 s   Phase 0: whole pizza → draw the halving line → lift one half
   *   2.5–5 s   Phase 1: re-form → draw quarters → lift one quarter (1/4)
   *   5–8 s     Phase 2: same quarters → lift 3 of 4 pieces (3/4) → fade back
   */
  const PERIOD = 8;
  const tMod = t % PERIOD;

  // Phase helpers (0..1 within each sub-phase)
  const phase0 = clamp(tMod / 2.5, 0, 1);                     // whole → half shown
  const phase1Start = 2.5;
  const phase1 = clamp((tMod - phase1Start) / 2.5, 0, 1);     // reform → 1/4 shown
  const phase2Start = 5.0;
  const phase2 = clamp((tMod - phase2Start) / 3.0, 0, 1);     // 1/4 → 3/4 shown

  // Which phase are we in? (affects label and lifted pieces)
  const inPhase0 = tMod < 2.5;
  const inPhase1 = tMod >= 2.5 && tMod < 5.0;
  const inPhase2 = tMod >= 5.0;

  // Pizza centre
  const cx = 380;
  const cy = 240;
  const R = 130;

  // --- Slice-line draw-on progress ---
  // Phase 0: one line cuts across (halving). Draw 0→1 in first 1 s of phase.
  const halfLineProg = inPhase0 ? drawOn(tMod, 1.0, 0.3) : 1;
  // Phase 1 & 2: quarter lines (two perpendicular lines)
  const quarterLine1Prog = (!inPhase0) ? drawOn(tMod - phase1Start, 0.8, 0.1) : 0;
  const quarterLine2Prog = (!inPhase0) ? drawOn(tMod - phase1Start, 0.8, 0.5) : 0;

  // --- Lift offsets for highlighted slices ---
  // Phase 0: top half lifts
  const halfLift = inPhase0 ? lerp(0, -34, easeOut(clamp((tMod - 1.1) / 0.8, 0, 1))) : 0;
  // Phase 1: one quarter lifts (top-right)
  const quarterLift = inPhase1 ? lerp(0, -30, easeOut(clamp((tMod - phase1Start - 0.9) / 0.7, 0, 1))) : 0;
  // Phase 2: three quarters lift
  const triLift = inPhase2 ? lerp(0, -28, easeOut(clamp((tMod - phase2Start - 0.5) / 0.8, 0, 1))) : 0;

  // --- Fraction text to display ---
  let fracNumer = '';
  let fracDenom = '';
  let fracAlpha = 0;
  if (inPhase0 && halfLineProg > 0.8) {
    fracNumer = '1'; fracDenom = '2';
    fracAlpha = clamp((tMod - 1.0) / 0.5, 0, 1);
  } else if (inPhase1 && quarterLine2Prog > 0.7) {
    fracNumer = '1'; fracDenom = '4';
    fracAlpha = clamp((tMod - phase1Start - 1.1) / 0.5, 0, 1);
  } else if (inPhase2) {
    fracNumer = '3'; fracDenom = '4';
    fracAlpha = clamp((tMod - phase2Start - 1.1) / 0.5, 0, 1);
  }

  // Gentle overall pizza bob
  const pizzaBob = wobble(t, 3.2, 3);

  // Helper: SVG arc path for a pie slice (angles in radians)
  const slicePath = (startAngle: number, endAngle: number, ox = 0, oy = 0) => {
    const x1 = cx + ox + R * Math.cos(startAngle);
    const y1 = cy + oy + pizzaBob + R * Math.sin(startAngle);
    const x2 = cx + ox + R * Math.cos(endAngle);
    const y2 = cy + oy + pizzaBob + R * Math.sin(endAngle);
    const large = endAngle - startAngle > Math.PI ? 1 : 0;
    return `M${cx + ox},${cy + oy + pizzaBob} L${x1},${y1} A${R},${R} 0 ${large},1 ${x2},${y2} Z`;
  };

  // Pepperoni positions (deterministic, by index)
  const pepperoni = [
    { r: 0.55, a: -0.6 }, { r: 0.55, a: 2.0 }, { r: 0.55, a: 3.7 },
    { r: 0.55, a: 5.2 },  { r: 0.28, a: 1.1 }, { r: 0.28, a: -1.4 },
    { r: 0.28, a: 4.5 },  { r: 0.75, a: 0.3 }, { r: 0.75, a: 2.8 },
  ];

  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="fs-bg" cx="50%" cy="50%"><stop offset="0%" stopColor="#fffbf0" /><stop offset="100%" stopColor="#fef3c7" /></radialGradient>
        <radialGradient id="fs-plate" cx="50%" cy="60%"><stop offset="0%" stopColor="#fff" /><stop offset="100%" stopColor="#e2e8f0" /></radialGradient>
        <radialGradient id="fs-crust" cx="50%" cy="50%"><stop offset="0%" stopColor="#fde68a" /><stop offset="60%" stopColor="#f59e0b" /><stop offset="100%" stopColor="#b45309" /></radialGradient>
        <radialGradient id="fs-cheese" cx="50%" cy="50%"><stop offset="0%" stopColor="#fef9c3" /><stop offset="80%" stopColor="#fde047" /></radialGradient>
        <filter id="fs-glow"><feGaussianBlur stdDeviation="6" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="url(#fs-bg)" />

      {/* Floating confetti-like dots for fun */}
      {[0,1,2,3,4,5,6,7].map(i => {
        const cx2 = 60 + i * 100;
        const cy2 = 30 + (i % 3) * 20 + wobble(t, 2.2 + i * 0.3, 8, i * 1.1);
        const colors = ['#f87171','#fb923c','#facc15','#4ade80','#60a5fa','#a78bfa','#f472b6','#34d399'];
        return <circle key={i} cx={cx2} cy={cy2} r={5} fill={colors[i]} opacity={0.6} />;
      })}

      {/* Plate shadow */}
      <ellipse cx={cx} cy={cy + pizzaBob + 115} rx={160} ry={18} fill="rgba(0,0,0,0.1)" />

      {/* Plate */}
      <circle cx={cx} cy={cy + pizzaBob} r={152} fill="url(#fs-plate)" stroke="#cbd5e1" strokeWidth={4} />
      <circle cx={cx} cy={cy + pizzaBob} r={140} fill="none" stroke="#e2e8f0" strokeWidth={3} />

      {/* ── Pizza body: render slices based on phase ─── */}
      {inPhase0 && (
        <>
          {/* Bottom half — stationary */}
          <path d={slicePath(0, Math.PI)} fill="url(#fs-crust)" stroke="#b45309" strokeWidth={2.5} />
          <path d={slicePath(0, Math.PI)} fill="url(#fs-cheese)" stroke="none" />
          {/* Top half — lifts to show "1/2" */}
          <path d={slicePath(Math.PI, Math.PI * 2, 0, halfLift)} fill="url(#fs-crust)" stroke="#b45309" strokeWidth={2.5}
            filter={halfLift < -5 ? 'url(#fs-glow)' : undefined} />
          <path d={slicePath(Math.PI, Math.PI * 2, 0, halfLift)} fill="url(#fs-cheese)" stroke="none" />
        </>
      )}
      {(inPhase1 || inPhase2) && (
        <>
          {/* Quadrant angles: 0, π/2, π, 3π/2, 2π */}
          {[0, 1, 2, 3].map(qi => {
            const startA = qi * (Math.PI / 2);
            const endA = (qi + 1) * (Math.PI / 2);
            // Determine lift for this quadrant
            let lift = 0;
            if (inPhase1 && qi === 3) lift = quarterLift;         // 1/4: top-right slice
            if (inPhase2 && qi !== 2) lift = triLift;             // 3/4: all but bottom-left
            const highlight = (inPhase1 && qi === 3) || (inPhase2 && qi !== 2);
            return (
              <g key={qi}>
                <path d={slicePath(startA, endA, 0, lift)}
                  fill="url(#fs-crust)" stroke="#b45309" strokeWidth={2.5}
                  filter={highlight && lift < -5 ? 'url(#fs-glow)' : undefined} />
                <path d={slicePath(startA, endA, 0, lift)}
                  fill="url(#fs-cheese)" stroke="none" />
              </g>
            );
          })}
        </>
      )}

      {/* Pepperoni dots drawn on top of cheese */}
      {pepperoni.map((p, i) => {
        const px = cx + R * p.r * Math.cos(p.a);
        const py = cy + pizzaBob + R * p.r * Math.sin(p.a);
        // Determine which quadrant to follow for lift
        const angleNorm = ((p.a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const qi = Math.floor(angleNorm / (Math.PI / 2));
        let liftY = 0;
        if (inPhase0 && p.a > 0 && p.a < Math.PI) liftY = halfLift;  // top half
        if (inPhase1 && qi === 3) liftY = quarterLift;
        if (inPhase2 && qi !== 2) liftY = triLift;
        return (
          <g key={i}>
            <circle cx={px} cy={py + liftY} r={9} fill="#dc2626" stroke="#991b1b" strokeWidth={1.5} />
            <circle cx={px - 2} cy={py + liftY - 2} r={2.5} fill="#fca5a5" opacity={0.7} />
          </g>
        );
      })}

      {/* Slice guide lines — draw on progressively */}
      {/* Halving line (phase 0) */}
      {inPhase0 && (() => {
        const lx1 = cx - R * halfLineProg;
        const lx2 = cx + R * halfLineProg;
        return (
          <line x1={lx1} y1={cy + pizzaBob} x2={lx2} y2={cy + pizzaBob}
            stroke="#b45309" strokeWidth={3} strokeDasharray="8 4" strokeLinecap="round" opacity={0.9} />
        );
      })()}
      {/* Quarter lines (phase 1 & 2) */}
      {(!inPhase0) && (
        <>
          <line x1={cx} y1={cy + pizzaBob - R * quarterLine1Prog}
                x2={cx} y2={cy + pizzaBob + R * quarterLine1Prog}
            stroke="#b45309" strokeWidth={3} strokeDasharray="8 4" strokeLinecap="round" opacity={0.9} />
          <line x1={cx - R * quarterLine2Prog} y1={cy + pizzaBob}
                x2={cx + R * quarterLine2Prog} y2={cy + pizzaBob}
            stroke="#b45309" strokeWidth={3} strokeDasharray="8 4" strokeLinecap="round" opacity={0.9} />
        </>
      )}

      {/* Big fraction display */}
      {fracAlpha > 0 && (
        <g transform="translate(620 230)" opacity={fracAlpha}>
          <rect x={-60} y={-80} width={120} height={140} rx={20}
            fill="rgba(255,255,255,0.92)" stroke={accentColor} strokeWidth={3} />
          <text x={0} y={-20} textAnchor="middle" fill={accentColor}
            fontSize={52} fontWeight={800} fontFamily="Inter,sans-serif">{fracNumer}</text>
          <line x1={-36} y1={4} x2={36} y2={4} stroke={accentColor} strokeWidth={4} strokeLinecap="round" />
          <text x={0} y={52} textAnchor="middle" fill={accentColor}
            fontSize={52} fontWeight={800} fontFamily="Inter,sans-serif">{fracDenom}</text>
        </g>
      )}

      {/* Little chef hat on top */}
      <g transform={`translate(155 ${180 + wobble(t, 2.8, 5)})`}>
        <ellipse cx={0} cy={0} rx={28} ry={10} fill="#fff" stroke="#cbd5e1" strokeWidth={2} />
        <rect x={-18} y={-32} width={36} height={34} rx={14} fill="#fff" stroke="#cbd5e1" strokeWidth={2} />
        <ellipse cx={0} cy={-32} rx={14} ry={7} fill="#fff" stroke="#cbd5e1" strokeWidth={2} />
        {/* Face */}
        <circle cx={-7} cy={-14} r={3} fill="#374151" />
        <circle cx={7} cy={-14} r={3} fill="#374151" />
        <path d="M-6,−4 Q0,2 6,-4" fill="none" stroke="#374151" strokeWidth={2} strokeLinecap="round" />
        <path d="M-6,-4 Q0,2 6,-4" fill="none" stroke="#374151" strokeWidth={2} strokeLinecap="round" />
      </g>

      {/* Friendly caption */}
      <g transform="translate(400 422)">
        <rect x={-260} y={-22} width={520} height={40} rx={20}
          fill="rgba(255,255,255,0.88)" stroke="#fed7aa" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#1e293b"
          fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ Counting: apples drop into a basket, counter climbs 1→5 then resets ━━━ */
export interface CountingSceneProps {
  opacity?: number;
  accentColor?: string;
  /** Short friendly caption. */
  label?: string;
  /** Seconds since the scene started. */
  t?: number;
}

export const CountingScene: React.FC<CountingSceneProps> = ({
  opacity = 1,
  accentColor = '#22c55e',
  t = 0,
  label = "Let's count together!",
}) => {
  /*
   * Full loop: ~6 s.  One apple drops every ~1s.
   *   Apple i lands at time i * 1.0 + 0.3.
   *   Counter shows how many have landed.
   *   After all 5, brief pause, then loop resets.
   */
  const PERIOD = 6;
  const tMod = t % PERIOD;
  const APPLE_COUNT = 5;
  const DROP_INTERVAL = 1.0;
  const DROP_DELAY = 0.3;          // first apple starts at 0.3s

  // Basket position
  const bx = 400;
  const by = 350;

  // How many apples have fully landed?
  const landed = Math.min(
    APPLE_COUNT,
    Math.floor(Math.max(0, tMod - DROP_DELAY) / DROP_INTERVAL),
  );

  // Big counter number
  const count = landed;

  // Counter "bounce" when count changes (pulse on count transitions)
  const timeSinceLast = Math.max(0, tMod - DROP_DELAY - (landed - 1) * DROP_INTERVAL);
  const counterBounce = landed > 0 ? lerp(1.5, 1.0, easeOut(clamp(timeSinceLast / 0.4, 0, 1))) : 1;

  // Background sky gradient
  const bgGreenTop = '#d1fae5';
  const bgGreenBot = '#ecfdf5';

  // Apple drop positions: for each apple, compute y based on current time
  const apples = Array.from({ length: APPLE_COUNT }, (_, i) => {
    const landTime = DROP_DELAY + i * DROP_INTERVAL;
    const startTime = landTime - 0.7;              // starts falling 0.7s before landing
    const progress = clamp((tMod - startTime) / 0.7, 0, 1);
    const easedProg = progress * progress;         // gravity ease-in (quadratic)

    // Landing spot: spread apples across basket, deterministic by index
    const landX = bx + (i - 2) * 28 + (i % 2 === 0 ? 5 : -5);
    const landY = by - 20 - (i % 2) * 8;          // slight pile effect
    const startY = 60 + (i * 14) % 30;            // stagger starting heights

    const x = lerp(120 + i * 100, landX, easeOut(progress));  // drift to basket
    const y = lerp(startY, landY, easedProg);

    // After landing the apple stays in basket
    const isLanded = tMod >= landTime;
    const finalX = landX;
    const finalY = landY;

    // Small squash on impact
    const squashTime = clamp((tMod - landTime) / 0.25, 0, 1);
    const scaleX = isLanded ? lerp(1.4, 1.0, easeOut(squashTime)) : 1;
    const scaleY = isLanded ? lerp(0.7, 1.0, easeOut(squashTime)) : 1;

    return { x: isLanded ? finalX : x, y: isLanded ? finalY : y, scaleX, scaleY, visible: tMod >= startTime };
  });

  // Cheering character (bunny) at side
  const cheerBounce = wobble(t, 0.5, 6);
  const earWiggle = wobble(t, 0.7, 8);

  // Stars around count for pop effect
  const starPop = landed > 0 ? clamp((tMod - (DROP_DELAY + (landed - 1) * DROP_INTERVAL)) / 0.35, 0, 1) : 0;

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="cs2-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={bgGreenTop} />
          <stop offset="100%" stopColor={bgGreenBot} />
        </linearGradient>
        <radialGradient id="cs2-apple" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#fca5a5" />
          <stop offset="60%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#991b1b" />
        </radialGradient>
        <radialGradient id="cs2-basket" cx="50%" cy="30%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="100%" stopColor="#d97706" />
        </radialGradient>
        <filter id="cs2-shadow"><feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.2" /></filter>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="url(#cs2-bg)" />

      {/* Ground strip */}
      <rect x={0} y={370} width={800} height={80} fill="#bbf7d0" />
      <ellipse cx={400} cy={370} rx={400} ry={14} fill="#86efac" opacity={0.7} />

      {/* Drifting clouds */}
      {[{ y: 55, phase: 0 }, { y: 90, phase: 3.1 }].map((c, i) => {
        const cx2 = ((t * 12 + i * 380) % 920) - 100;
        return (
          <g key={i} transform={`translate(${cx2} ${c.y})`} opacity={0.85}>
            <ellipse cx={0} cy={0} rx={40} ry={24} fill="#fff" />
            <ellipse cx={38} cy={6} rx={30} ry={20} fill="#fff" />
            <ellipse cx={-32} cy={8} rx={26} ry={18} fill="#fff" />
          </g>
        );
      })}

      {/* Apple tree (static, decorative) */}
      <g transform="translate(130 290)">
        <rect x={-8} y={-60} width={16} height={65} rx={6} fill="#92400e" />
        <ellipse cx={0} cy={-75} rx={52} ry={50} fill="#16a34a" opacity={0.9} />
        <ellipse cx={-16} cy={-88} rx={32} ry={26} fill="#22c55e" opacity={0.8} />
        <ellipse cx={18} cy={-95} rx={28} ry={22} fill="#22c55e" opacity={0.7} />
        {/* A couple of static apples on tree */}
        {[{ x: -14, y: -82 }, { x: 12, y: -70 }].map((pos, i) => (
          <g key={i}>
            <circle cx={pos.x} cy={pos.y} r={10} fill="#ef4444" />
            <line x1={pos.x} y1={pos.y - 10} x2={pos.x + 3} y2={pos.y - 16} stroke="#16a34a" strokeWidth={2} strokeLinecap="round" />
          </g>
        ))}
      </g>

      {/* Falling / landed apples */}
      {apples.map((apple, i) =>
        apple.visible ? (
          <g key={i} transform={`translate(${apple.x} ${apple.y}) scale(${apple.scaleX} ${apple.scaleY})`}>
            {/* Apple shadow (only when in basket) */}
            {apple.scaleX === 1 && <ellipse cx={0} cy={17} rx={14} ry={5} fill="rgba(0,0,0,0.15)" />}
            {/* Body */}
            <circle cx={0} cy={0} r={16} fill="url(#cs2-apple)" filter="url(#cs2-shadow)" />
            {/* Shine */}
            <ellipse cx={-5} cy={-6} rx={4} ry={6} fill="rgba(255,255,255,0.45)" />
            {/* Stem */}
            <line x1={0} y1={-16} x2={2} y2={-24} stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
            {/* Leaf */}
            <path d="M2,-22 Q10,-30 16,-20 Q8,-18 2,-22 Z" fill="#16a34a" />
          </g>
        ) : null
      )}

      {/* Basket */}
      <g transform={`translate(${bx} ${by})`}>
        {/* Basket body */}
        <path d="M-70,0 Q-72,50 -50,60 L50,60 Q72,50 70,0 Z"
          fill="url(#cs2-basket)" stroke="#b45309" strokeWidth={3} />
        {/* Basket weave lines */}
        {[-40, -10, 20].map(wx => (
          <line key={wx} x1={wx} y1={2} x2={wx - 6} y2={58} stroke="#b45309" strokeWidth={2} opacity={0.5} />
        ))}
        {[6, 22, 38].map(wy => (
          <line key={wy} x1={-68 + wy * 0.3} y1={wy} x2={68 - wy * 0.3} y2={wy} stroke="#b45309" strokeWidth={2} opacity={0.4} />
        ))}
        {/* Basket rim */}
        <path d="M-72,0 Q-70,-16 0,-18 Q70,-16 72,0" fill="none" stroke="#b45309" strokeWidth={5} strokeLinecap="round" />
        <path d="M-72,0 Q-70,-10 0,-12 Q70,-10 72,0" fill="#d97706" stroke="none" />
        {/* Handle */}
        <path d="M-42,-18 Q-42,-50 0,-52 Q42,-50 42,-18"
          fill="none" stroke="#92400e" strokeWidth={5} strokeLinecap="round" />
      </g>

      {/* Cheering bunny character */}
      <g transform={`translate(660 ${300 + cheerBounce})`}>
        {/* Body */}
        <ellipse cx={0} cy={20} rx={28} ry={35} fill="#fde68a" stroke="#f59e0b" strokeWidth={2} />
        {/* Head */}
        <circle cx={0} cy={-20} r={28} fill="#fde68a" stroke="#f59e0b" strokeWidth={2} />
        {/* Left ear */}
        <ellipse cx={-14 + earWiggle * 0.3} cy={-52} rx={8} ry={22} fill="#fde68a" stroke="#f59e0b" strokeWidth={2}
          transform={`rotate(${-10 + earWiggle} -14 -20)`} />
        <ellipse cx={-14 + earWiggle * 0.3} cy={-52} rx={4} ry={16} fill="#fca5a5"
          transform={`rotate(${-10 + earWiggle} -14 -20)`} />
        {/* Right ear */}
        <ellipse cx={14 - earWiggle * 0.3} cy={-52} rx={8} ry={22} fill="#fde68a" stroke="#f59e0b" strokeWidth={2}
          transform={`rotate(${10 - earWiggle} 14 -20)`} />
        <ellipse cx={14 - earWiggle * 0.3} cy={-52} rx={4} ry={16} fill="#fca5a5"
          transform={`rotate(${10 - earWiggle} 14 -20)`} />
        {/* Eyes */}
        <circle cx={-10} cy={-22} r={4} fill="#1e293b" />
        <circle cx={10} cy={-22} r={4} fill="#1e293b" />
        <circle cx={-8} cy={-24} r={1.5} fill="#fff" />
        <circle cx={12} cy={-24} r={1.5} fill="#fff" />
        {/* Smile */}
        <path d="M-10,-10 Q0,-4 10,-10" fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
        {/* Nose */}
        <ellipse cx={0} cy={-12} rx={4} ry={3} fill="#fca5a5" />
        {/* Arms raised in cheer */}
        <path d={`M-26,8 Q-44,${-10 + cheerBounce * 0.5} -36,${-28 + cheerBounce}`}
          fill="none" stroke="#f59e0b" strokeWidth={7} strokeLinecap="round" />
        <path d={`M26,8 Q44,${-10 + cheerBounce * 0.5} 36,${-28 + cheerBounce}`}
          fill="none" stroke="#f59e0b" strokeWidth={7} strokeLinecap="round" />
        {/* Cheek blush */}
        <ellipse cx={-18} cy={-14} rx={7} ry={5} fill="#fca5a5" opacity={0.5} />
        <ellipse cx={18} cy={-14} rx={7} ry={5} fill="#fca5a5" opacity={0.5} />
      </g>

      {/* Big counter display */}
      <g transform="translate(90 160)">
        <rect x={-55} y={-68} width={110} height={110} rx={22}
          fill="rgba(255,255,255,0.92)" stroke={accentColor} strokeWidth={4}
          filter="url(#cs2-shadow)" />
        {/* Star bursts when count increments */}
        {starPop > 0 && count > 0 && [0,1,2,3,4,5].map(si => {
          const a = (si / 6) * Math.PI * 2 + Math.PI / 6;
          const r = lerp(0, 52, easeOut(starPop));
          return (
            <line key={si}
              x1={0} y1={0}
              x2={r * Math.cos(a)} y2={r * Math.sin(a)}
              stroke={accentColor} strokeWidth={3} strokeLinecap="round"
              opacity={1 - starPop} />
          );
        })}
        <text x={0} y={14} textAnchor="middle" fill={accentColor}
          fontSize={68} fontWeight={900} fontFamily="Inter,sans-serif"
          transform={`scale(${counterBounce})`}>{count}</text>
      </g>

      {/* Friendly caption */}
      <g transform="translate(400 422)">
        <rect x={-235} y={-22} width={470} height={40} rx={20}
          fill="rgba(255,255,255,0.88)" stroke="#bbf7d0" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#1e293b"
          fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
